import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Field } from '../../components/controls';
import { Icon, type IconName } from '../../components/Icon';
import { formatPounds } from '../../domain/calculator';
import type { SubmissionRequest, SubmissionResponse } from '../../domain/contract';
import { newSubmissionId } from '../../domain/ids';
import { track } from '../../lib/analytics';
import { api, ApiError, withRetry } from '../../lib/api';
import { mountMeetingsEmbed, parseBookingMessage } from '../../lib/booking';
import { currentJourney } from '../../lib/journey';
import { answersOf, companyName, totalFor, type Flow } from '../state';
import { CompanyField, type ScreenProps } from './Steps';

export const PRIVACY_URL = 'https://peoplexyz.ai/privacy-policy';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildRequest(flow: Flow, submissionId: string): SubmissionRequest {
  const journey = currentJourney();
  const waitlist = flow.evaluation?.view.result === 'waitlist';
  return {
    submissionId,
    journeyId: journey.journeyId,
    answers: answersOf(flow),
    hires: flow.hires,
    contact: { firstName: flow.firstName.trim(), lastName: flow.lastName.trim(), email: flow.email.trim().toLowerCase() },
    companyName: companyName(flow),
    consent: true,
    marketingOptIn: waitlist ? false : flow.marketing,
    waitOption: waitlist ? flow.waitOption : null,
    attribution: journey.attribution,
  };
}

function sameLogicalRequest(a: SubmissionRequest, b: SubmissionRequest): boolean {
  return JSON.stringify({ ...a, submissionId: '' }) === JSON.stringify({ ...b, submissionId: '' });
}

/**
 * Sends the final submission. The submission id is created before the first
 * attempt and reused for every retry of the same logical submission, so a lost
 * response can never create a second submission or reservation.
 */
export async function submitFlow(flow: Flow, update: (patch: Partial<Flow>) => void): Promise<SubmissionResponse> {
  const previous = flow.pendingRequest;
  let request = buildRequest(flow, previous?.submissionId ?? newSubmissionId());
  if (previous && !sameLogicalRequest(previous, request)) request = { ...request, submissionId: newSubmissionId() };
  update({ pendingRequest: request });
  try {
    return await withRetry(() => api.submit(request));
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      const fresh = { ...request, submissionId: newSubmissionId() };
      update({ pendingRequest: fresh });
      return withRetry(() => api.submit(fresh));
    }
    throw error;
  }
}

export function afterSubmit(response: SubmissionResponse): Partial<Flow> {
  return {
    submission: response,
    pendingRequest: null,
    evaluation: { settingsVersionId: response.settingsVersionId, regionName: null, view: response.view },
    screen: response.view.result === 'waitlist' ? 'done' : response.booking.observedAt ? 'done' : 'booking',
    meetingAt: response.booking.meetingAt,
  };
}

function Summary({ flow }: { flow: Flow }) {
  const view = flow.evaluation?.view;
  if (!view) return null;
  const [label, value, amount] =
    view.result === 'eligible'
      ? [
          'Your place',
          `${view.programmeName} · Cohort ${view.cohortNumber}`,
          formatPounds(flow.hires > 1 ? totalFor(view.package.firstHire, view.package.additionalHire, flow.hires) : view.package.firstHire),
        ]
      : view.result === 'check_needed'
        ? ['Your result', 'Check needed, a short call', '']
        : ['Your choice', flow.waitOption === 'talk' ? 'Talk to us about hiring' : 'Waitlist for the next intake', ''];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '14px 16px',
        border: '1px solid rgba(0,0,0,.14)',
        borderRadius: 14,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="eyebrow mute">{label}</div>
        <div className="s15" style={{ fontWeight: 600, marginTop: 3 }}>
          {value}
        </div>
      </div>
      <span className="num" style={{ fontSize: 18, fontWeight: 700, flexShrink: 0 }}>
        {amount}
      </span>
    </div>
  );
}

export function Secure({ flow, update, headingRef }: ScreenProps) {
  const view = flow.evaluation?.view;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const resumed = useRef(false);
  const waitlist = view?.result === 'waitlist';

  async function send() {
    setBusy(true);
    setFailure(null);
    try {
      const response = await submitFlow(flow, update);
      track('submission_completed', { step: 'secure', result: response.view.result });
      update(afterSubmit(response));
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        setErrors(mapFieldErrors(error.fields));
        setFailure('Check the highlighted details and try again.');
      } else {
        setFailure("We couldn't send your details. Check your connection and try again. Your answers are saved.");
      }
    } finally {
      setBusy(false);
    }
  }

  // Only on mount: a refresh while a submission was in flight resends the same
  // request (same submission id) to recover its result.
  const pendingAtMount = useRef(flow.pendingRequest != null && flow.submission == null);
  useEffect(() => {
    if (resumed.current || !pendingAtMount.current) return;
    resumed.current = true;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submit() {
    const e: Record<string, string> = {};
    if (!flow.firstName.trim()) e.firstName = 'Enter your first name.';
    if (!flow.lastName.trim()) e.lastName = 'Enter your last name.';
    if (!EMAIL.test(flow.email.trim())) e.email = 'Enter a valid work email.';
    if (!flow.company && !(flow.companyUnlisted && flow.companyQuery.trim())) e.company = 'Enter your company name.';
    if (!flow.consent) e.consent = 'Tick to confirm before you continue.';
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    void send();
  }

  if (!view) return null;
  const label =
    view.result === 'eligible'
      ? 'Secure my place and book a call'
      : view.result === 'check_needed'
        ? 'Book my call'
        : flow.waitOption === 'talk'
          ? 'Talk to us'
          : 'Join the waitlist';

  return (
    <section className="sec">
      <Summary flow={flow} />
      <h1 className="disp h2" style={{ marginTop: 24 }} ref={headingRef} tabIndex={-1}>
        {view.result === 'eligible' ? 'Secure your place' : view.result === 'check_needed' ? 'Book your call' : 'Leave your details'}
      </h1>
      <p className="s15 mute" style={{ lineHeight: 1.5, marginTop: 6 }}>
        {waitlist ? "We'll use these to get back to you." : 'Your details, then a time for your 20-minute call.'}
      </p>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        style={{ display: 'grid', gap: 16, marginTop: 20 }}
      >
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="First name" error={errors.firstName}>
            {({ inputId, describedBy, invalid }) => (
              <input
                id={inputId}
                className="inp"
                value={flow.firstName}
                onChange={(e) => update({ firstName: e.target.value })}
                autoComplete="given-name"
                placeholder="Maya"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
            )}
          </Field>
          <Field label="Last name" error={errors.lastName}>
            {({ inputId, describedBy, invalid }) => (
              <input
                id={inputId}
                className="inp"
                value={flow.lastName}
                onChange={(e) => update({ lastName: e.target.value })}
                autoComplete="family-name"
                placeholder="Okonkwo"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
            )}
          </Field>
        </div>
        <Field label="Work email" error={errors.email}>
          {({ inputId, describedBy, invalid }) => (
            <input
              id={inputId}
              className="inp"
              type="email"
              value={flow.email}
              onChange={(e) => update({ email: e.target.value })}
              autoComplete="email"
              placeholder="you@company.co.uk"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <CompanyField flow={flow} update={update} error={errors.company} />
        <div style={{ display: 'grid', gap: 2 }}>
          <Checkbox
            className="chk"
            checked={flow.consent}
            onChange={(consent) => update({ consent })}
            describedBy={errors.consent ? 'consent-error' : undefined}
          >
            I confirm these details are accurate and agree to PeopleXYZ contacting me about this funded place.{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener">
              Privacy notice
            </a>
            .
          </Checkbox>
          {errors.consent ? (
            <span id="consent-error" className="err" style={{ paddingLeft: 32 }}>
              {errors.consent}
            </span>
          ) : null}
          {waitlist ? null : (
            <Checkbox className="chk" checked={flow.marketing} onChange={(marketing) => update({ marketing })}>
              Send me occasional updates from PeopleXYZ.
            </Checkbox>
          )}
        </div>
        {failure ? (
          <div className="notice" role="alert">
            {failure}
          </div>
        ) : null}
        <div style={{ marginTop: 4 }}>
          <Button type="submit" busy={busy}>
            {label}
          </Button>
          {view.result === 'eligible' ? (
            <p className="s13 mute" style={{ textAlign: 'center', marginTop: 10 }}>
              We'll hold your place for 14 days while we agree terms.
            </p>
          ) : null}
        </div>
      </form>
    </section>
  );
}

function mapFieldErrors(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, message] of Object.entries(fields)) {
    if (key.includes('email')) out.email = 'Enter a valid work email.';
    else if (key.includes('firstName')) out.firstName = 'Enter your first name.';
    else if (key.includes('lastName')) out.lastName = 'Enter your last name.';
    else if (key.includes('company')) out.company = message;
  }
  return out;
}

/**
 * The HubSpot Meetings embed, injected only after the submission is durably
 * accepted. The booking message is a hint the server records and reconciles.
 */
export function Booking({ flow, settings, update, headingRef }: ScreenProps) {
  const submission = flow.submission;
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    if (!submission?.bookingUrl) return;
    mountMeetingsEmbed();
    async function onMessage(event: MessageEvent) {
      const booking = parseBookingMessage(event, settings.bookingOrigins);
      if (!booking || !submission) return;
      setRecording(true);
      try {
        await withRetry(() => api.booking(submission.submissionId, booking.meetingAt), 4);
      } catch {
        /* the reconciler cannot see the browser; the team follows up from HubSpot if this is lost */
      }
      track('booking_completed', { step: 'booking', result: submission.view.result });
      update({ meetingAt: booking.meetingAt, screen: 'done' });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [submission, settings.bookingOrigins, update]);

  if (!submission) return null;
  return (
    <section className="sec">
      <Summary flow={flow} />
      <div
        className="s14"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 16,
          padding: '10px 14px',
          background: '#F7F8FA',
          borderRadius: 10,
        }}
      >
        <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {submission.firstName} {submission.lastName} · {submission.companyName}
        </span>
        <Icon name="check" size={16} style={{ color: '#629677' }} />
      </div>
      <h1 className="disp h2" style={{ marginTop: 20 }} ref={headingRef} tabIndex={-1}>
        Choose a time for your 20-minute call
      </h1>
      <p className="s14 mute" style={{ marginTop: 6 }} aria-live="polite">
        {recording
          ? 'Saving your booking.'
          : submission.view.result === 'eligible'
            ? 'Your place is held. Pick a time that suits you.'
            : 'Pick a time that suits you.'}
      </p>
      <div className="booking-frame">
        <div className="meetings-iframe-container" data-src={submission.bookingUrl ?? ''} data-title="Book your funded hiring place call" />
      </div>
      <button type="button" className="lnk" onClick={() => update({ bookingDeferred: true, screen: 'done' })}>
        I'll book later
      </button>
    </section>
  );
}

export function Done({ flow, update, headingRef }: ScreenProps & { onRestart: () => void }) {
  const submission = flow.submission;
  if (!submission) return null;
  const view = submission.view;
  const email = submission.email;
  const when = flow.meetingAt ? formatMeeting(flow.meetingAt) : null;
  let content: [IconName, string, string];
  if (view.result === 'eligible') {
    content = [
      'check',
      `You've successfully secured your place in Cohort ${view.cohortNumber}.`,
      when ? `We've emailed your summary to ${email}. Speak soon on ${when}.` : `We'll email your summary to ${email}, with a link to book your call.`,
    ];
  } else if (view.result === 'check_needed') {
    content = when
      ? ['calendar-check', 'Your call is booked.', `We've emailed the details to ${email}. Speak soon on ${when}.`]
      : ['calendar-check', "Thanks, we've got your details.", `We'll email ${email} with a link to book your call.`];
  } else if (submission.waitOption === 'talk') {
    content = ['mail', "Thanks, we'll be in touch.", `We'll email ${email} to arrange a conversation about hiring with PeopleXYZ.`];
  } else {
    content = ['check', "You're on the waitlist.", `We'll email ${email} when the next intake opens.`];
  }
  return (
    <section className="sec">
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <span
          style={{
            width: 56,
            height: 56,
            margin: '0 auto',
            borderRadius: '50%',
            background: '#629677',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={content[0]} size={28} />
        </span>
        <h1 className="disp hr" style={{ marginTop: 20, textWrap: 'balance' }} ref={headingRef} tabIndex={-1}>
          {content[1]}
        </h1>
        <p className="s15 mute" style={{ lineHeight: 1.55, margin: '12px auto 0', maxWidth: '40ch', textWrap: 'pretty' }}>
          {content[2]}
        </p>
        <button
          type="button"
          className="s14 mute"
          onClick={() => update({ screen: 'landing' })}
          style={{ margin: '24px auto 0', display: 'flex', minHeight: 44, alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer' }}
          data-restart
        >
          <Icon name="rotate-ccw" size={14} /> Start a new check
        </button>
      </div>
    </section>
  );
}

function formatMeeting(iso: string): string {
  const date = new Date(iso);
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date))
    p[part.type] = part.value;
  return `${p.weekday} ${p.day} ${p.month} at ${p.hour}:${p.minute}`;
}
